<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" />

                <!-- Modal -->
                <div
                    ref="modalRef"
                    class="relative w-full max-w-xl max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl"
                    :style="modalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing shrink-0">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="handleClose"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <!-- Success State -->
                    <div v-if="submitted" class="px-5 pt-6 pb-8 sm:px-6 flex flex-col items-center text-center gap-4">
                        <div class="w-14 h-14 rounded-full bg-blue-600/15 border border-blue-500/30 flex items-center justify-center shrink-0">
                            <Clapperboard class="w-7 h-7 text-blue-400" />
                        </div>
                        <div>
                            <h2 class="text-xl font-bold text-white mb-1">Your Pitch has been Submitted</h2>
                            <p class="text-white/50 text-sm leading-relaxed">
                                Your pitch is now under review. Our team will reach out to the requested performers and assess feasibility.
                            </p>
                        </div>
                        <div class="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3.5 text-left space-y-2.5">
                            <div class="flex items-start gap-2.5">
                                <span class="text-blue-400 text-base leading-none mt-0.5">1</span>
                                <p class="text-white/60 text-sm leading-snug">Your request enters a review queue where our team will assess feasibility and next steps.</p>
                            </div>
                            <div class="flex items-start gap-2.5">
                                <span class="text-blue-400 text-base leading-none mt-0.5">2</span>
                                <p class="text-white/60 text-sm leading-snug">The more community support your casting gets, the stronger the signal to make it happen.</p>
                            </div>
                            <div class="flex items-start gap-2.5">
                                <span class="text-blue-400 text-base leading-none mt-0.5">3</span>
                                <p class="text-white/60 text-sm leading-snug">Stay tuned — we'll keep you updated as your casting progresses.</p>
                            </div>
                            <div class="flex items-start gap-2.5">
                                <span class="text-blue-400 text-base leading-none mt-0.5">4</span>
                                <p class="text-white/60 text-sm leading-snug">All submissions have 30 days from the time they are approved to meet their support goal. Submissions that that do not meet the support goal, will be closed after 30 days.</p>
                            </div>
                        </div>
                        <button
                            @click="$emit('close')"
                            class="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-sm transition-all active:scale-95"
                        >
                            Done
                        </button>
                    </div>

                    <!-- Content -->
                    <div v-else class="px-5 pt-3 pb-5 sm:px-5 sm:pt-5 sm:pb-6 overflow-y-auto min-h-0 casting-scroll">

                        <!-- Heading -->
                        <div class="mb-4">
                            <img src="https://sm.naughtycdn.com/images/www/naughty-america-logo.png" alt="Naughty America" class="h-3.5 w-auto mb-2 opacity-60" />
                            <h2 class="text-2xl font-extrabold leading-tight pr-8">
                                <span class="text-white">Submit Your </span><span class="text-violet-400">Production Pitch</span>
                            </h2>
                            <div class="mt-2 mb-2.5 h-px bg-white/10" />
                            <p class="text-white/45 text-xs leading-relaxed">Help shape the future of our content. Share your creative ideas and help us decide what premium productions get made next.</p>
                        </div>

                        <!-- Title -->
                        <div class="mb-3">
                            <label class="text-white text-xs font-bold block mb-0.5">
                                Pitch Title
                            </label>
                            <p class="text-white/40 text-[11px] mb-1.5 leading-snug">Give your pitch a name that hooks people.</p>
                            <input
                                v-model="form.title"
                                type="text"
                                placeholder="e.g. The Tutor Next Door"
                                maxlength="120"
                                :disabled="formDisabled"
                                :class="[
                                    'w-full px-3.5 py-2.5 bg-zinc-900 border rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                    fieldErrors.title ? 'border-red-500/60 hover:border-red-500/80 focus:border-red-500' : 'border-white/10 hover:border-white/20 focus:border-blue-500'
                                ]"
                            />
                            <p v-if="fieldErrors.title" class="text-red-400 text-xs mt-1">{{ fieldErrors.title }}</p>
                        </div>

                        <!-- Description / The Setup -->
                        <div class="mb-3">
                            <label class="text-white text-xs font-bold block mb-0.5">The Setup</label>
                            <p class="text-white/40 text-[11px] mb-1.5 leading-snug">One or two sentences that sell your concept — this is what people see in the feed.</p>
                            <textarea
                                v-model="form.description"
                                placeholder="e.g. A college student hires a private tutor, but the lessons quickly go off-syllabus when she realizes he's the guy she's been flirting with at the coffee shop..."
                                rows="3"
                                maxlength="1000"
                                :disabled="formDisabled"
                                :class="[
                                    'w-full px-3.5 py-2.5 bg-zinc-900 border rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none transition-colors resize-none disabled:opacity-50 disabled:cursor-not-allowed',
                                    fieldErrors.description ? 'border-red-500/60 hover:border-red-500/80 focus:border-red-500' : 'border-white/10 hover:border-white/20 focus:border-blue-500'
                                ]"
                            />
                            <div class="flex justify-between items-start mt-1">
                                <p v-if="fieldErrors.description" class="text-red-400 text-xs">{{ fieldErrors.description }}</p>
                                <div :class="['text-[11px] text-white/30', fieldErrors.description ? 'ml-auto' : 'w-full text-right']">{{ form.description.length }}/1000</div>
                            </div>
                        </div>

                        <!-- Performers -->
                        <div class="mb-3">
                            <label class="text-white text-xs font-bold block mb-0.5">Cast</label>
                            <p class="text-white/40 text-[11px] mb-1.5 leading-snug">Pick from available performers or name specific performers.</p>
                            <div class="relative">
                                <div :class="[
                                    'bg-zinc-900 border rounded-xl px-3 py-2',
                                    fieldErrors.performers
                                        ? 'border-red-500/60 focus-within:border-red-500'
                                        : performerInput.trim().length >= 2 && !suggestions.length && !isLoadingPerformers
                                            ? 'border-red-500/60'
                                            : 'border-white/10 focus-within:border-blue-500'
                                ]">
                                    <!-- Selected performers -->
                                    <div v-if="form.performers.length" class="flex flex-wrap gap-1.5 mb-2">
                                        <span
                                            v-for="performer in form.performers"
                                            :key="performer.id"
                                            class="inline-flex items-center gap-1.5 bg-blue-600/20 border border-blue-500/30 text-blue-300 text-xs font-medium pl-0.5 pr-2 py-0.5 rounded-full"
                                        >
                                            <img
                                                :src="performer.thumbnail_url"
                                                :alt="performer.name"
                                                class="w-5 h-5 rounded-full object-cover object-top shrink-0"
                                            />
                                            {{ performer.name }}
                                            <button
                                                type="button"
                                                @click="removePerformer(performer.id)"
                                                :disabled="formDisabled"
                                                class="text-blue-400/60 hover:text-blue-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <X class="w-3 h-3" />
                                            </button>
                                        </span>
                                    </div>
                                    <div v-if="form.performers.length < 4" class="flex items-center gap-2">
                                        <input
                                            v-model="performerInput"
                                            type="text"
                                            placeholder="Search your cast…"
                                            autocomplete="off"
                                            :disabled="formDisabled"
                                            class="flex-1 bg-transparent border-0 text-white text-sm placeholder:text-white/30 outline-none ring-0 focus:outline-none focus:ring-0 focus:border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                            @input="onPerformerSearch"
                                            @keydown="onPerformerKeydown"
                                        />
                                        <svg v-if="isLoadingPerformers" class="animate-spin w-3.5 h-3.5 text-white/40 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                        </svg>
                                    </div>
                                    <p v-else class="text-white/30 text-xs py-1">Maximum of 4 performers reached</p>
                                </div>

                                <!-- Suggestions dropdown -->
                                <div
                                    v-if="suggestions.length"
                                    class="absolute z-10 w-full mt-1 bg-zinc-900 border border-white/10 rounded-xl overflow-hidden shadow-xl"
                                >
                                    <button
                                        v-for="(performer, index) in suggestions"
                                        :key="performer.id"
                                        type="button"
                                        @mousedown.prevent
                                        @click="selectPerformer(performer)"
                                        :class="[
                                            'w-full text-left px-3 py-2 text-sm text-white transition-colors flex items-center gap-2.5',
                                            index === highlightedPerformerIndex ? 'bg-zinc-700' : 'hover:bg-zinc-800'
                                        ]"
                                    >
                                        <div class="w-9 h-9 rounded-full overflow-hidden ring-1 ring-white/10 shrink-0 bg-zinc-800">
                                            <img
                                                :src="performer.thumbnail_url"
                                                :alt="performer.name"
                                                class="w-full h-full object-cover object-top scale-125 origin-top"
                                            />
                                        </div>
                                        <span>{{ performer.name }}</span>
                                    </button>
                                </div>

                                <!-- No results -->
                                <p v-if="performerInput.trim().length >= 2 && !suggestions.length && !isLoadingPerformers" class="text-white/30 text-[11px] mt-1">
                                    No performers found
                                </p>
                                <!-- Field error -->
                                <p v-if="fieldErrors.performers || performerLimitError" class="text-red-400 text-xs mt-1">{{ fieldErrors.performers || performerLimitError }}</p>
                            </div>
                        </div>

                        <!-- Performer Description -->
                        <div v-if="!form.performers.length" class="mb-3">
                            <p class="text-white/70 text-[10px] font-bold uppercase tracking-wider mb-1.5">Or name specific performers</p>
                            <input
                                v-model="form.performerDescription"
                                type="text"
                                placeholder='e.g. "Brunette MILF, athletic male lead"'
                                maxlength="200"
                                :disabled="formDisabled"
                                :class="[
                                    'w-full px-3.5 py-2.5 bg-zinc-900 border rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                    fieldErrors.performer_description ? 'border-red-500/60 hover:border-red-500/80 focus:border-red-500' : 'border-white/10 hover:border-white/20 focus:border-blue-500'
                                ]"
                            />
                            <p v-if="fieldErrors.performer_description" class="text-red-400 text-xs mt-1">{{ fieldErrors.performer_description }}</p>
                        </div>

                        <!-- Format -->
                        <div class="mb-3">
                            <label class="text-white/70 text-xs font-semibold block mb-1.5">Format</label>
                            <div class="grid grid-cols-4 gap-1.5">
                                <button
                                    v-for="option in formatOptions"
                                    :key="option"
                                    type="button"
                                    :disabled="formDisabled"
                                    @click="form.format = form.format === option ? null : option"
                                    :class="[
                                        'py-2 rounded-xl text-xs font-semibold border transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed',
                                        form.format === option
                                            ? 'bg-blue-600/30 text-blue-300 border-blue-500/50'
                                            : 'bg-zinc-900 text-white/60 border-white/10 hover:border-white/20 hover:text-white'
                                    ]"
                                >
                                    {{ option }}
                                </button>
                            </div>
                        </div>

                        <!-- Tags -->
                        <div class="mb-3">
                            <label class="text-white text-xs font-bold block mb-0.5">Category / Tags</label>
                            <p class="text-white/40 text-[11px] mb-1.5 leading-snug">Help people find your pitch — select all that apply.</p>
                            <div class="relative">
                                <div :class="[
                                    'bg-zinc-900 border rounded-xl px-3 py-2',
                                    tagInput.trim().length >= 2 && !tagSuggestions.length && !isLoadingTags
                                        ? 'border-red-500/60'
                                        : 'border-white/10 focus-within:border-blue-500'
                                ]">
                                    <div v-if="form.tags.length" class="flex flex-wrap gap-1.5 mb-2">
                                        <span
                                            v-for="tag in form.tags"
                                            :key="tag.id"
                                            class="inline-flex items-center gap-1 bg-zinc-800 border border-white/10 text-white/70 text-xs font-medium px-2 py-0.5 rounded-full"
                                        >
                                            #{{ tag.name }}
                                            <button
                                                type="button"
                                                :disabled="formDisabled"
                                                @click="removeTag(tag.id)"
                                                class="text-white/30 hover:text-white/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <X class="w-3 h-3" />
                                            </button>
                                        </span>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <input
                                            v-model="tagInput"
                                            type="text"
                                            placeholder="Search tags…"
                                            autocomplete="off"
                                            :disabled="formDisabled"
                                            class="flex-1 bg-transparent border-0 text-white text-sm placeholder:text-white/30 outline-none ring-0 focus:outline-none focus:ring-0 focus:border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                            @keydown.escape="tagInput = ''"
                                        />
                                        <svg v-if="isLoadingTags" class="animate-spin w-3.5 h-3.5 text-white/40 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                        </svg>
                                    </div>
                                </div>

                                <!-- Tag suggestions dropdown -->
                                <div
                                    v-if="tagSuggestions.length"
                                    class="absolute z-10 w-full mt-1 bg-zinc-900 border border-white/10 rounded-xl overflow-hidden shadow-xl"
                                >
                                    <button
                                        v-for="tag in tagSuggestions"
                                        :key="tag.id"
                                        type="button"
                                        @mousedown.prevent
                                        @click="selectTag(tag)"
                                        class="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-zinc-800 transition-colors"
                                    >
                                        #{{ tag.name }}
                                    </button>
                                </div>

                                <!-- No results -->
                                <p v-if="tagInput.trim().length >= 2 && !tagSuggestions.length && !isLoadingTags" class="text-white/30 text-[11px] mt-1">
                                    No tags found
                                </p>
                            </div>
                        </div>

                        <!-- Setting -->
                        <div class="mb-3">
                            <label class="text-white/70 text-xs font-semibold block mb-1.5">Setting</label>
                            <p class="text-white/40 text-[11px] mb-1.5 leading-snug">Where does the scene take place?</p>
                            <div class="flex flex-wrap gap-1.5 mb-2">
                                <button
                                    v-for="option in settingOptions"
                                    :key="option"
                                    type="button"
                                    :disabled="formDisabled"
                                    @click="toggleSetting(option)"
                                    :class="[
                                        'px-3 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed',
                                        form.setting.includes(option)
                                            ? 'bg-blue-600/30 text-blue-300 border-blue-500/50'
                                            : 'bg-zinc-900 text-white/60 border-white/10 hover:border-white/20 hover:text-white'
                                    ]"
                                >
                                    {{ option }}
                                </button>
                                <button
                                    type="button"
                                    :disabled="formDisabled"
                                    @click="showCustomSetting = !showCustomSetting"
                                    :class="[
                                        'px-3 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed',
                                        showCustomSetting
                                            ? 'bg-blue-600/30 text-blue-300 border-blue-500/50'
                                            : 'bg-zinc-900 text-white/60 border-white/10 hover:border-white/20 hover:text-white'
                                    ]"
                                >
                                    Custom
                                </button>
                            </div>
                            <input
                                v-if="showCustomSetting"
                                v-model="settingCustom"
                                type="text"
                                placeholder='Describe your setting — e.g. "Rooftop bar at sunset"'
                                maxlength="120"
                                :disabled="formDisabled"
                                :class="[
                                    'w-full px-3.5 py-2.5 bg-zinc-900 border rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                    fieldErrors.setting ? 'border-red-500/60 hover:border-red-500/80 focus:border-red-500' : 'border-white/10 hover:border-white/20 focus:border-blue-500'
                                ]"
                            />
                            <p v-if="fieldErrors.setting" class="text-red-400 text-xs mt-1">{{ fieldErrors.setting }}</p>
                        </div>

                        <!-- Director's Notes -->
                        <div class="mb-4">
                            <button
                                v-if="!showDirectorNotes"
                                type="button"
                                :disabled="formDisabled"
                                @click="showDirectorNotes = true"
                                class="w-full py-2.5 border border-dashed border-white/15 rounded-xl text-white/50 text-xs font-semibold hover:border-white/30 hover:text-white/70 transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span class="text-base leading-none">+</span>
                                Add Director's Notes
                                <span class="text-white/30 font-normal">(optional)</span>
                            </button>
                            <template v-else>
                                <label class="text-white text-xs font-bold block mb-0.5">Director's Notes</label>
                                <p class="text-white/40 text-[11px] mb-1.5 leading-snug">Describe the arc, pacing, key moments, or anything that brings your vision to life.</p>
                                <textarea
                                    v-model="form.directorNotes"
                                    placeholder="e.g. Starts slow with tension building over tutoring — eye contact, accidental touches. She makes the first move…"
                                    rows="3"
                                    maxlength="2000"
                                    :disabled="formDisabled"
                                    :class="[
                                        'w-full px-3.5 py-2.5 bg-zinc-900 border rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none transition-colors resize-none disabled:opacity-50 disabled:cursor-not-allowed',
                                        fieldErrors.director_notes ? 'border-red-500/60 hover:border-red-500/80 focus:border-red-500' : 'border-white/10 hover:border-white/20 focus:border-blue-500'
                                    ]"
                                />
                                <p v-if="fieldErrors.director_notes" class="text-red-400 text-xs mt-1">{{ fieldErrors.director_notes }}</p>
                            </template>
                        </div>

                        <!-- Terms agreement -->
                        <label class="mb-3 flex items-start gap-2.5 cursor-pointer w-fit" @click.prevent="agreedToTerms = !agreedToTerms">
                            <span
                                :class="[
                                    'mt-0.5 shrink-0 w-4 h-4 rounded border transition-colors flex items-center justify-center',
                                    agreedToTerms
                                        ? 'bg-blue-600 border-blue-500'
                                        : 'bg-zinc-900 border-white/20 hover:border-white/40'
                                ]"
                            >
                                <svg v-if="agreedToTerms" class="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                                    <path d="M1 4l3 3 5-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </span>
                            <p class="text-white/50 text-[11px] leading-snug select-none">
                                By submitting, I agree to the
                                <a @click.stop href="https://www.naughtyamerica.com/terms-of-service.html" target="_blank" rel="noopener" class="text-blue-400 hover:text-blue-300 underline">Terms of Service</a>
                                and
                                <a @click.stop href="https://www.naughtyamerica.com/privacy-policy.html" target="_blank" rel="noopener" class="text-blue-400 hover:text-blue-300 underline">Privacy Policy</a>.
                            </p>
                        </label>

                        <!-- Error -->
                        <p v-if="errorMessage" class="text-red-400 text-sm text-center mb-3">{{ errorMessage }}</p>

                        <!-- Similar casting warning -->
                        <div v-if="similarCasting" class="mb-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                            <p class="text-amber-400 text-xs font-bold mb-1">Similar pitch already active</p>
                            <p class="text-white/60 text-xs leading-snug mb-2">
                                "<span class="text-white/80">{{ similarCasting.title }}</span>" is already open and accepting support. Are you sure you want to submit a new one?
                            </p>
                            <div class="grid grid-cols-3 gap-2">
                                <button
                                    @click="$emit('dismissSimilar')"
                                    class="py-2 border border-white/15 bg-transparent text-white/70 font-semibold rounded-lg text-xs hover:bg-white/5 transition-all"
                                >
                                    Edit
                                </button>
                                <button
                                    @click="$emit('close')"
                                    class="py-2 border border-white/15 bg-transparent text-white/40 font-semibold rounded-lg text-xs hover:bg-white/5 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    @click="handleSubmitForce"
                                    :disabled="loading"
                                    class="py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg text-xs transition-all active:scale-95 disabled:opacity-50"
                                >
                                    Submit Anyway
                                </button>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div v-else>
                            <div class="grid grid-cols-2 gap-2">
                                <button
                                    @click="handleClose"
                                    class="py-2.5 border border-white/15 bg-transparent text-white font-semibold rounded-xl text-xs hover:bg-white/5 transition-all active:scale-95"
                                >
                                    Cancel
                                </button>
                                <button
                                    @click="handleSubmit"
                                    :disabled="!canSubmit || loading"
                                    :class="[
                                        'h-[42px] font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-1.5',
                                        canSubmit && !loading
                                            ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 active:scale-95'
                                            : 'bg-zinc-800 text-white/30 cursor-not-allowed'
                                    ]"
                                >
                                    <template v-if="loading">
                                        <svg class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Submitting…
                                    </template>
                                    <template v-else>
                                        <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                                        <span>{{ castingCost }}</span>
                                        <span class="opacity-40">·</span>
                                        Submit your pitch
                                    </template>
                                </button>
                            </div>

                            <!-- Non-logged-in user legend -->
                            <p v-if="!isUserLoggedIn" class="text-white/40 text-[11px] text-center mt-3 leading-snug">
                                You need a free account to submit your pitch
                            </p>
                        </div>

                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { X, Clapperboard } from 'lucide-vue-next'
import axios from 'axios'

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

// Draft cookie — persists form data across subdomains (www and members)
const DRAFT_COOKIE = 'casting_draft'

const saveDraftCookie = () => {
    const data = {
        form: form.value,
        showCustomSetting: showCustomSetting.value,
        settingCustom: settingCustom.value,
        showDirectorNotes: showDirectorNotes.value,
    }
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString()

    // Determine domain for cookie (works for both local and production)
    const hostname = window.location.hostname
    let domainPart = ''

    if (hostname.includes('naughtyamerica.com')) {
        domainPart = '; domain=.naughtyamerica.com'
    }

    const cookieString = `${DRAFT_COOKIE}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires}; path=/${domainPart}; SameSite=Lax`
    document.cookie = cookieString

    console.log('[CreateCastingModal] Draft saved:', { hostname, domain: domainPart, cookieString: cookieString.substring(0, 100) + '...' })
}

const readDraftCookie = () => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + DRAFT_COOKIE + '=([^;]*)'))
    if (!match) {
        console.log('[CreateCastingModal] No draft cookie found')
        return null
    }
    try {
        const data = JSON.parse(decodeURIComponent(match[1]))
        console.log('[CreateCastingModal] Draft loaded:', data)
        return data
    } catch (e) {
        console.error('[CreateCastingModal] Failed to parse draft cookie:', e)
        return null
    }
}

const clearDraftCookie = () => {
    const hostname = window.location.hostname
    let domainPart = ''

    if (hostname.includes('naughtyamerica.com')) {
        domainPart = '; domain=.naughtyamerica.com'
    }

    document.cookie = `${DRAFT_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart}`
    console.log('[CreateCastingModal] Draft cleared:', { hostname, domain: domainPart })
}

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    userBalance: {
        type: Number,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    errorMessage: {
        type: String,
        default: '',
    },
    fieldErrors: {
        type: Object,
        default: () => ({}),
    },
    submitted: {
        type: Boolean,
        default: false,
    },
    castingCost: {
        type: Number,
        default: 5,
    },
    similarCasting: {
        type: Object,
        default: null,
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close', 'submit', 'dismissSimilar', 'buyStars'])

const formatOptions = ['Standard', 'VR', 'Vertical', 'Mixed Reality']

const settingOptions = ['Bedroom', 'Office', 'Pool / Outdoors', 'Hotel Room', 'Gym / Locker Room', 'Kitchen', 'Living Room', 'Classroom', 'Massage Studio']

const showCustomSetting = ref(false)
const settingCustom = ref('')
const showDirectorNotes = ref(false)
const agreedToTerms = ref(false)

const toggleSetting = (option) => {
    const idx = form.value.setting.indexOf(option)
    if (idx === -1) form.value.setting.push(option)
    else form.value.setting.splice(idx, 1)
}

const form = ref({
    title: '',
    performers: [],
    performerDescription: '',
    format: null,
    tags: [],
    description: '',
    setting: [],
    directorNotes: '',
    starsCommitted: props.castingCost,
})

// Performers — loaded once on open, filtered client-side
const performerInput = ref('')
const allPerformers = ref([])
const isLoadingPerformers = ref(false)

const loadPerformers = async () => {
    if (allPerformers.value.length > 0) return
    isLoadingPerformers.value = true
    try {
        const { data } = await axios.get('/feed/api/casting-performers')
        allPerformers.value = data
    } finally {
        isLoadingPerformers.value = false
    }
}

const suggestions = computed(() => {
    const q = performerInput.value.trim().toLowerCase()
    if (!q) return []
    const selectedIds = new Set(form.value.performers.map(p => p.id))
    return allPerformers.value
        .filter(p => !selectedIds.has(p.id) && p.name.toLowerCase().includes(q))
        .slice(0, 10)
})

const onPerformerSearch = () => { /* filtering handled by computed */ }

const performerLimitError = ref('')

const selectPerformer = (performer) => {
    if (form.value.performers.length >= 4) return
    form.value.performers.push(performer)
    form.value.performerDescription = ''
    performerInput.value = ''
}

const removePerformer = (id) => {
    form.value.performers = form.value.performers.filter(p => p.id !== id)
    performerLimitError.value = ''
}

const closeSuggestions = () => {
    performerInput.value = ''
    highlightedPerformerIndex.value = -1
}

const highlightedPerformerIndex = ref(-1)

watch(suggestions, () => {
    highlightedPerformerIndex.value = -1
})

const onPerformerKeydown = (e) => {
    if (e.key === 'Escape') {
        closeSuggestions()
        return
    }

    if (!suggestions.value.length) return

    if (e.key === 'ArrowDown') {
        e.preventDefault()
        highlightedPerformerIndex.value = Math.min(highlightedPerformerIndex.value + 1, suggestions.value.length - 1)
    } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        highlightedPerformerIndex.value = Math.max(highlightedPerformerIndex.value - 1, -1)
    } else if (e.key === 'Enter' && highlightedPerformerIndex.value >= 0) {
        e.preventDefault()
        selectPerformer(suggestions.value[highlightedPerformerIndex.value])
    }
}

// Tags — all fantasies loaded once on open, filtered client-side
const tagInput = ref('')
const allTags = ref([])
const isLoadingTags = ref(false)

const loadTags = async () => {
    if (allTags.value.length > 0) return
    isLoadingTags.value = true
    try {
        const { data } = await axios.get('/feed/api/casting-tags')
        allTags.value = data
    } finally {
        isLoadingTags.value = false
    }
}

const tagSuggestions = computed(() => {
    const q = tagInput.value.trim().toLowerCase()
    if (!q) return []
    const selectedIds = new Set(form.value.tags.map(t => t.id))
    return allTags.value
        .filter(t => !selectedIds.has(t.id) && t.name.toLowerCase().includes(q))
        .slice(0, 10)
})

const selectTag = (tag) => {
    form.value.tags.push(tag)
    tagInput.value = ''
}

const removeTag = (id) => {
    form.value.tags = form.value.tags.filter(t => t.id !== id)
}

const resetForm = () => {
    form.value = {
        title: '',
        performers: [],
        performerDescription: '',
        format: null,
        tags: [],
        description: '',
        setting: [],
        directorNotes: '',
        starsCommitted: props.castingCost,
    }
    performerInput.value = ''
    tagInput.value = ''
    showCustomSetting.value = false
    settingCustom.value = ''
    showDirectorNotes.value = false
    agreedToTerms.value = false
}

watch(() => props.isOpen, (open) => {
    if (open) {
        loadPerformers()
        loadTags()
        const draft = readDraftCookie()
        if (draft) {
            form.value = { ...form.value, ...draft.form }
            showCustomSetting.value = draft.showCustomSetting ?? false
            settingCustom.value = draft.settingCustom ?? ''
            showDirectorNotes.value = draft.showDirectorNotes ?? false
        }
    } else {
        resetForm()
    }
})

const formDisabled = computed(() => props.loading || !!props.similarCasting)

const hasEnoughBalance = computed(() => props.userBalance >= props.castingCost)

const canSubmit = computed(() => {
    return (form.value.performers.length > 0 || form.value.performerDescription.trim().length > 0)
        && form.value.description.trim().length > 0
        && form.value.format !== null
        && agreedToTerms.value
})

const handleSubmit = () => {
    if (!canSubmit.value) return

    // Always save draft to prevent data loss
    saveDraftCookie()

    if (props.isUserLoggedIn && !hasEnoughBalance.value) {
        emit('buyStars')
        return
    }
    const setting = [
        ...form.value.setting,
        ...(showCustomSetting.value && settingCustom.value.trim() ? [settingCustom.value.trim()] : []),
    ]
    emit('submit', { ...form.value, setting })
}

const handleSubmitForce = () => {
    const setting = [
        ...form.value.setting,
        ...(showCustomSetting.value && settingCustom.value.trim() ? [settingCustom.value.trim()] : []),
    ]
    emit('submit', { ...form.value, setting, force: true })
}

// Touch/swipe to dismiss
const modalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const modalStyle = computed(() => {
    if (!isDragging.value) return {}
    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {}
    return { transform: `translateY(${deltaY}px)`, transition: 'none' }
})

const handleTouchStart = (e) => {
    const touch = e.touches[0]
    const rect = modalRef.value?.getBoundingClientRect()
    if (!rect || touch.clientY - rect.top > 60) return
    touchStartY.value = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return
    touchCurrentY.value = e.touches[0].clientY
    if (touchCurrentY.value - touchStartY.value > 0) e.preventDefault()
}

const handleTouchEnd = () => {
    if (!isDragging.value) return
    const deltaY = touchCurrentY.value - touchStartY.value
    // If dragged down more than 100px, close the modal
    if (deltaY > 100) {
        emit('close')
    }
    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}

const handleClose = () => {
    clearDraftCookie()
    emit('close')
}

// Expose clearDraftCookie so parent components can call it
defineExpose({
    clearDraftCookie
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.casting-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.12) transparent;
}
.casting-scroll::-webkit-scrollbar {
    width: 4px;
}
.casting-scroll::-webkit-scrollbar-track {
    background: transparent;
}
.casting-scroll::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.12);
    border-radius: 9999px;
}
.casting-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(255,255,255,0.22);
}
</style>
